import config from '../config';

// The Documentation page's way into the developer portal (backend metaplan G4; contract
// apiv2/developer-portal.openapi.yaml): POST /v2/edit/developer_portal/handoff/ answers
// {url, expires_in} — a one-time link, valid about a minute, that opens a short-lived,
// docs-only portal session for the signed-in user. Refusals are the M9 envelope {code, message, …}.
//
// The portal opens in a NEW TAB: the docs are served by the backend on the developer host
// (developer.touroptima.com), which cannot be framed (frames denied, SameSite cookies). The page
// used to iframe the public docs copy, which is retired at the docs cutover (owner action O-9).

export const HANDOFF_PATH = '/developer_portal/handoff/';

// POST with the dashboard session token. Resolves {status, body} (body: the parsed JSON or null);
// rejects only on a network error. Plain fetch, not the data provider: its 401/403 handler signs
// the user out, and a 403 here (not_permitted) is an answer, not an expired session.
export async function postDocsHandoff(token) {
    const res = await fetch(`${config.apiUrl}${HANDOFF_PATH}`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
        },
        body: '{}',
    });
    const body = await res.json().catch(() => null);
    return { status: res.status, body };
}

// Only an absolute https URL is opened (http too where allowHttp: local development).
export function portalTarget(raw, { allowHttp = false } = {}) {
    if (typeof raw !== 'string') { return null; }
    let url;
    try {
        url = new URL(raw);
    } catch (_e) {
        return null;
    }
    if (url.protocol === 'https:' || (allowHttp && url.protocol === 'http:')) { return url.toString(); }
    return null;
}

// Until the docs cutover (owner action O-9) production has no developer portal yet: the backend
// answers 503 developer_portal_disabled (or 404, before a backend with this endpoint is deployed),
// and the page opens the public documentation copy it used to frame instead. O-9 switches the portal
// on and only then retires that copy, so the page works before, during and after the cutover.
// Remove this fallback once O-9 is done.
export const PUBLIC_DOCS_URL = 'https://dzalc1e35t21c.cloudfront.net/index.html';

export function portalNotLive(status, body) {
    return status === 404 || (status === 503 && !!body && body.code === 'developer_portal_disabled');
}

// Copy per failure: the backend's envelope codes, plus the page's own.
export const DOCS_FAILURES = {
    not_permitted: 'Your account may not open the developer documentation. Ask your administrator for the "Developer: read" permission.',
    // The page's own:
    unexpected_url: 'The server answered with an unexpected address, so the dashboard did not open it.',
    unreachable: 'The documentation could not be opened. Try again.',
};

export const CLIENT_FAILURES = ['unexpected_url', 'unreachable'];

export function describeDocsFailure(code) {
    return DOCS_FAILURES[code] || DOCS_FAILURES.unreachable;
}
